import { alpha } from "@mui/material/styles";
import { type FC, useMemo } from "react";
import { NavLink } from "react-router";

import {
  bgColorByBaseCategory,
  colorByBaseCategory,
} from "~/constants/baseCategory";
import { ROUTES } from "~/constants/routing";
import { useCategories } from "~/providers/CategoriesProvider";
import type { BaseCategory } from "~/types/baseCategory";

import * as Styled from "./CategoryChip.styles";

interface ICategoryChipProps {
  categoryId?: string;
  interactive?: boolean;
  appearance?: "list" | "row";
}

export const CategoryChip: FC<ICategoryChipProps> = ({
  categoryId,
  interactive = true,
  appearance = "list",
}) => {
  const { getCategoryNameById, getCategoryColorById, getCategorySlugById } =
    useCategories();
  const categoryName = useMemo(
    () => (categoryId ? getCategoryNameById(categoryId) : ""),
    [categoryId, getCategoryNameById]
  );

  const categorySlug = useMemo(
    () => (categoryId ? getCategorySlugById(categoryId) : ""),
    [categoryId, getCategorySlugById]
  );

  const { categoryColor, categoryBgColor } = useMemo(() => {
    const categoryColor = colorByBaseCategory[categorySlug as BaseCategory];
    const categoryBgColor = bgColorByBaseCategory[categorySlug as BaseCategory];

    if (!categoryColor || !categoryBgColor) {
      const color = categoryId ? getCategoryColorById(categoryId) : "";
      const bgColor = color ? alpha(color, 0.15) : "";
      return { categoryColor: color, categoryBgColor: bgColor };
    }
    return { categoryColor, categoryBgColor };
  }, [categoryId, categorySlug, getCategoryColorById]);

  if (!categoryId) {
    return null;
  }

  const rowStyle = appearance === "row" ? "category" : undefined;

  return (
    <Styled.StyledBox $rowStyle={rowStyle}>
      {interactive ? (
        <Styled.StyledChip
          label={categoryName}
          component={NavLink}
          to={ROUTES.category.createURL(categorySlug)}
          prefetch="intent"
          $color={categoryColor}
          $bgColor={categoryBgColor}
          $rowStyle={rowStyle}
          size="small"
          variant="outlined"
        />
      ) : (
        <Styled.StyledChip
          label={categoryName}
          $color={categoryColor}
          $bgColor={categoryBgColor}
          $rowStyle={rowStyle}
          size="small"
          variant="outlined"
        />
      )}
    </Styled.StyledBox>
  );
};
